import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiGet, apiPost } from '../lib/api.js';
import AIDialSelectedModal from '../components/AIDialSelectedModal.jsx';
import { useAuth } from '../store/auth.js';

import LeadsHeader from './leads/LeadsHeader.jsx';
import LeadsFilterBar from './leads/LeadsFilterBar.jsx';
import { dispositionFilterOptions, snapshotFilters, applySavedFilters } from './leads/leadFilterOptions.js';
import LeadsBulkActionBar from './leads/LeadsBulkActionBar.jsx';
import LeadsTable from './leads/LeadsTable.jsx';
import CsvExportWizard from './leads/CsvExportWizard.jsx';
import {
  CreateLeadModal,
  ManageDispositionsModal,
} from './leads/LeadModals.jsx';
import {
  BulkSmsModal,
  BulkEmailModal,
  DispositionToBotModal,
  AddToPipelineModal,
  TransferToLeadAssistModal,
} from './leads/BulkActionModals.jsx';

export default function Leads() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const openId = params.get('open');
  const token = useAuth((s) => s.token);
  const isAdmin = useAuth((s) => ['admin', 'superadmin'].includes(s.agent?.role));
  const isSuperAdmin = useAuth((s) => (s.agent?.role === 'superadmin' || s.user?.role === 'superadmin'));
  const [directOnly, setDirectOnly] = useState(false);

  useEffect(() => {
    if (openId) navigate(`/leads/${openId}`, { replace: true });
  }, [openId, navigate]);

  // ── Filter state ────────────────────────────────────────────────────────
  const [assignee, setAssignee] = useState('all');
  const [vendors, setVendors] = useState([]);
  const [states, setStates] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [onlyShow, setOnlyShow] = useState('');
  const [onlyShowDays, setOnlyShowDays] = useState('');
  const [onlyShowZip, setOnlyShowZip] = useState('');
  const [onlyShowBirthYear, setOnlyShowBirthYear] = useState('');
  const [onlyShowBirthMonth, setOnlyShowBirthMonth] = useState('');
  const [dispositionTags, setDispositionTags] = useState([]);
  const [excludeDispositions, setExcludeDispositions] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [pageSize, setPageSize] = useState(() => {
    const n = parseInt(params.get('pageSize') || '', 10);
    return Number.isFinite(n) && n > 0 && n <= 500 ? n : 50;
  });
  const [page, setPage] = useState(() => {
    const n = parseInt(params.get('page') || '', 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  });
  const [sort, setSort] = useState('-createdAt');

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Sync state into URL query params
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (page > 1) next.set('page', String(page));
    else next.delete('page');
    if (pageSize !== 50) next.set('pageSize', String(pageSize));
    else next.delete('pageSize');
    setParams(next, { replace: true });
  }, [page, pageSize, setParams]);

  // ── Reference data ──────────────────────────────────────────────────────
  const { data: agentsData } = useQuery({
    queryKey: ['agents', directOnly],
    queryFn: () => apiGet('/agents', directOnly ? { directOnly: 'true' } : {}),
  });
  const agents = agentsData?.agents || [];

  const { data: dripsData } = useQuery({
    queryKey: ['marketing-drips'],
    queryFn: () => apiGet('/marketing/drips'),
  });
  const drips = useMemo(() => {
    const raw = Array.isArray(dripsData) ? dripsData : (dripsData?.drips ?? []);
    return raw.map((d) => ({
      _id: d.dripGroupId || d._id,
      dripGroupId: d.dripGroupId || d._id,
      name: d.name,
      trigger: d.trigger,
      dispositionTriggers: d.dispositionTriggers || [],
    }));
  }, [dripsData]);

  const { data: campaignsData } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => apiGet('/campaigns'),
  });
  const campaigns = Array.isArray(campaignsData) ? campaignsData : (campaignsData?.campaigns ?? []);

  const { data: pipelinesData } = useQuery({
    queryKey: ['pipelines'],
    queryFn: () => apiGet('/pipelines'),
  });
  const pipelines = useMemo(() => {
    const p = pipelinesData?.pipelines ?? pipelinesData;
    if (Array.isArray(p)) return p;
    if (p && typeof p === 'object') return [...(p.mine || []), ...(p.company || [])];
    return [];
  }, [pipelinesData]);

  const { data: vendorsData } = useQuery({
    queryKey: ['vendors'],
    queryFn: () => apiGet('/vendors'),
  });
  const vendorOptions = useMemo(
    () => (vendorsData?.vendors || []).map((v) => ({ value: v._id, label: v.name })),
    [vendorsData]
  );

  const { data: aiBotsData } = useQuery({
    queryKey: ['ai-bots'],
    queryFn: () => apiGet('/aibots'),
  });
  const aiBots = aiBotsData?.bots || [];

  const { data: disposData, refetch: refetchDispos } = useQuery({
    queryKey: ['dispositions'],
    queryFn: () => apiGet('/dispositions'),
  });
  const dispositions = disposData?.dispositions || [];
  const dispositionOptions = useMemo(
    () => dispositionFilterOptions(dispositions),
    [dispositions]
  );

  // ── Modals & bulk actions ───────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const [showCustomDispoModal, setShowCustomDispoModal] = useState(false);
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const [aiDialOpen, setAiDialOpen] = useState(false);
  const [dispoToBotOpen, setDispoToBotOpen] = useState(false);
  const [addToPipelineOpen, setAddToPipelineOpen] = useState(false);
  const [transferToLeadAssistOpen, setTransferToLeadAssistOpen] = useState(false);
  const [bulkSmsOpen, setBulkSmsOpen] = useState(false);
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [csvWizardOpen, setCsvWizardOpen] = useState(false);
  const [csvWizardIds, setCsvWizardIds] = useState(null);

  // Selection
  const [selected, setSelected] = useState(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);

  const queryParams = useMemo(
    () => ({
      page,
      limit: pageSize,
      sort,
      assignee,
      vendor: vendors.join(','),
      state: states.join(','),
      timezone: timezones.join(','),
      onlyShow: onlyShow || undefined,
      onlyShowDays: onlyShowDays || undefined,
      onlyShowZip: onlyShowZip || undefined,
      onlyShowBirthYear: onlyShowBirthYear || undefined,
      onlyShowBirthMonth: onlyShowBirthMonth || undefined,
      dispositionTags: dispositionTags.join(','),
      excludeDispositions: excludeDispositions.join(','),
      status: statuses.join(',') || undefined,
      q: debouncedSearch,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      directOnly: directOnly ? 'true' : undefined,
    }),
    [
      page, pageSize, sort, assignee, vendors, states, timezones,
      onlyShow, onlyShowDays, onlyShowZip, onlyShowBirthYear, onlyShowBirthMonth,
      dispositionTags, excludeDispositions, statuses,
      debouncedSearch, dateFrom, dateTo, directOnly,
    ]
  );

  const { data, isLoading } = useQuery({
    queryKey: ['leads', queryParams],
    queryFn: () => apiGet('/leads', queryParams),
    keepPreviousData: true,
  });
  const leads = data?.leads || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  const orderedSelectedIds = useMemo(() => {
    const rowOrder = leads.filter((l) => selected.has(l._id)).map((l) => l._id);
    const seen = new Set(rowOrder);
    const rest = [];
    for (const id of selected) if (!seen.has(id)) rest.push(id);
    return [...rowOrder, ...rest];
  }, [leads, selected]);

  useEffect(() => {
    setSelected(new Set());
    setAllMatching(false);
  }, [JSON.stringify(queryParams)]); // eslint-disable-line react-hooks/exhaustive-deps

  // SSE: real-time new-lead push, bound to the reactive store token so a login,
  // logout or account switch re-subscribes. EventSource cannot send headers, so
  // each (re)connect first trades the session for a 60s single-purpose stream
  // ticket (normal Bearer auth, shared axios client): a stale session 401s there
  // once, the interceptor logs out, and no stream request is made with it.
  useEffect(() => {
    if (!token) return undefined;
    const base = import.meta.env.VITE_API_URL || '/api';
    const MAX_RETRIES = 5;
    let es = null;
    let retries = 0;
    let timer = null;
    let stopped = false;

    const stop = () => {
      stopped = true;
      clearTimeout(timer);
      try { es?.close(); } catch (_) {}
      es = null;
    };

    const retry = () => {
      if (stopped) return;
      if (retries >= MAX_RETRIES) { stop(); return; }
      timer = setTimeout(connect, Math.min(30000, 1000 * 2 ** retries++));
    };

    const connect = async () => {
      if (stopped) return;
      let ticket;
      try {
        const r = await apiPost('/leads/stream-ticket', {});
        ticket = r?.ticket || r?.data?.ticket;
      } catch (err) {
        const st = err?.response?.status;
        if (st === 401 || st === 403) { stop(); return; } // interceptor handles 401 logout
        retry();
        return;
      }
      if (stopped || !ticket) return;
      try {
        es = new EventSource(`${base}/leads/stream?ticket=${encodeURIComponent(ticket)}`);
      } catch {
        return;
      }
      es.onopen = () => { retries = 0; };
      es.onmessage = (e) => {
        try {
          const d = JSON.parse(e.data);
          if (d?.event === 'lead:created') {
            qc.invalidateQueries({ queryKey: ['leads'] });
          }
        } catch (_) {}
      };
      es.onerror = () => {
        // Close the browser's uncontrolled reconnect loop; a fresh ticket is
        // minted on every attempt.
        try { es?.close(); } catch (_) {}
        es = null;
        retry();
      };
    };

    connect();
    return stop;
  }, [qc, token]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setAllMatching(false);
  };

  const toggleSelectAll = () => {
    if (leads.length === 0) return;
    const allPageSelected = leads.every((l) => selected.has(l._id));
    if (allPageSelected) {
      setSelected(new Set());
      setAllMatching(false);
    } else {
      setSelected(new Set(leads.map((l) => l._id)));
      setAllMatching(false);
    }
  };

  const clearSelection = () => {
    setSelected(new Set());
    setAllMatching(false);
  };

  const selectAllMatching = async () => {
    setSelectingAll(true);
    try {
      const p = { ...queryParams, idsOnly: true };
      delete p.page;
      delete p.limit;
      const res = await apiGet('/leads', p);
      setSelected(new Set(res.ids || []));
      setAllMatching(true);
      if (res.capped) {
        toast(`Selected the first ${(res.ids || []).length.toLocaleString()} of ${Number(res.total || 0).toLocaleString()} — narrow the filter to act on the rest.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not select all matching leads');
    } finally {
      setSelectingAll(false);
    }
  };

  // ── Actions ─────────────────────────────────────────────────────────────
  const bulkAction = useMutation({
    mutationFn: ({ action, payload }) =>
      apiPost('/leads/bulk/action', { ids: orderedSelectedIds, action, payload }),
    onSuccess: (res, vars) => {
      const added   = res.modified ?? res.added ?? res.matched ?? 0;
      const skipped = res.skipped ?? 0;
      const deleted = res.deleted ?? 0;
      const actionLabels = {
        enrollDrip:     `Added ${added} lead(s) to drip campaign${skipped ? `, skipped ${skipped} already enrolled` : ''}`,
        enrollCampaign: `Added ${added} lead(s) to campaign${skipped ? `, skipped ${skipped} already enrolled` : ''}`,
        enrollPipeline: `Moved ${added} lead(s) into pipeline`,
        delete:         `Deleted ${deleted} lead(s)${deleted < orderedSelectedIds.length ? `, ${orderedSelectedIds.length - deleted} skipped (pool-protected or not yours)` : ''}`,
      };
      toast.success(actionLabels[vars.action] || `${vars.action} applied to ${added || deleted} lead(s)`);
      // Disposition → Drip Action: say how many drips started.
      if (vars.action === 'addDisposition' && res.dripsStarted > 0) toast.success(`Started ${res.dripsStarted} drip enrollment(s)`);
      // Birthday texts: tell the agent what happened and which contacts can't be greeted yet.
      if (vars.action === 'enrollBirthdayTexts') {
        if (res.birthdayDripsFound === 0) toast('No Birthday Drip exists yet. Create one in Marketing Hub → Drips, then enroll again.', { icon: '🎂', duration: 7000 });
        else toast.success(`Enrolled ${res.birthdayEnrolled} lead(s) in your Birthday Drip`);
        if (res.missingDob > 0) toast(`Note: ${res.missingDob} contact(s) have no Date of Birth. Add one for the Birthday Drip to trigger.`, { icon: '⚠️', duration: 8000 });
      }
      clearSelection();
      setBulkMenuOpen(false);
      qc.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Bulk action failed'),
  });

  const bulkAssignTextBot = useMutation({
    mutationFn: ({ botId }) =>
      apiPost('/leads/bulk/assign-text-bot', { leadIds: orderedSelectedIds, botId }),
    onSuccess: (res) => {
      const modified = res.modified ?? res.matched ?? 0;
      toast.success(
        res.botId
          ? `Assigned AI text bot to ${modified} lead(s)`
          : `Unassigned AI text bot from ${modified} lead(s)`
      );
      clearSelection();
      setBulkMenuOpen(false);
      qc.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Assign text bot failed'),
  });

  const openCsvWizard = (idSet = null) => {
    setCsvWizardIds(idSet);
    setCsvWizardOpen(true);
  };

  const exportParamsFromFilters = () => ({
    assignee,
    state: states.join(','),
    timezone: timezones.join(','),
    onlyShow: onlyShow || '',
    onlyShowDays: onlyShowDays || '',
    onlyShowZip: onlyShowZip || '',
    onlyShowBirthYear: onlyShowBirthYear || '',
    onlyShowBirthMonth: onlyShowBirthMonth || '',
    dispositionTags: dispositionTags.join(','),
    excludeDispositions: excludeDispositions.join(','),
    status: statuses.join(','),
    q: debouncedSearch,
    dateFrom: dateFrom || '',
    dateTo: dateTo || '',
  });

  const savedSnapshot = snapshotFilters({
    vendors, states, timezones, statuses, dispositionTags, excludeDispositions,
    onlyShow, onlyShowDays, onlyShowZip, onlyShowBirthYear, onlyShowBirthMonth,
    search, dateFrom, dateTo, sort,
  });

  /** Re-apply a saved search through the page's filter setters. */
  const handleApplySaved = (f) => applySavedFilters(f, {
    vendors: setVendors, states: setStates, timezones: setTimezones, statuses: setStatuses,
    dispositionTags: setDispositionTags, excludeDispositions: setExcludeDispositions,
    onlyShow: setOnlyShow, onlyShowDays: setOnlyShowDays, onlyShowZip: setOnlyShowZip,
    onlyShowBirthYear: setOnlyShowBirthYear, onlyShowBirthMonth: setOnlyShowBirthMonth,
    search: setSearch, dateFrom: setDateFrom, dateTo: setDateTo, sort: setSort, page: setPage,
  });

  const handleResetFilters = () => {
    setVendors([]); setStates([]); setTimezones([]);
    setOnlyShow(''); setOnlyShowDays(''); setOnlyShowZip('');
    setOnlyShowBirthYear(''); setOnlyShowBirthMonth('');
    setDispositionTags([]); setExcludeDispositions([]);
    setStatuses([]); setSort('-createdAt');
    setSearch(''); setDateFrom(''); setDateTo('');
    setPage(1);
  };

  return (
    <div className="space-y-4 max-w-[1600px] mx-auto pb-24">
      {/* Header */}
      <LeadsHeader
        assignee={assignee}
        setAssignee={setAssignee}
        setPage={setPage}
        agents={agents}
        isAdmin={isAdmin}
        isSuperAdmin={isSuperAdmin}
        directOnly={directOnly}
        onToggleDirectOnly={(val) => {
          setDirectOnly(val);
          setPage(1);
          setAssignee('all');
        }}
        onOpenCsvWizard={() => openCsvWizard(null)}
        onUploadCsv={() => navigate('/leads/import')}
        onCreateLead={() => setShowCreate(true)}
      />

      {/* Filter bar */}
      <LeadsFilterBar
        dateFrom={dateFrom} setDateFrom={setDateFrom}
        dateTo={dateTo} setDateTo={setDateTo}
        sort={sort} setSort={setSort}
        pageSize={pageSize} setPageSize={setPageSize}
        vendors={vendors} setVendors={setVendors} vendorOptions={vendorOptions}
        states={states} setStates={setStates}
        timezones={timezones} setTimezones={setTimezones}
        onlyShow={onlyShow} setOnlyShow={setOnlyShow}
        onlyShowDays={onlyShowDays} setOnlyShowDays={setOnlyShowDays}
        onlyShowZip={onlyShowZip} setOnlyShowZip={setOnlyShowZip}
        onlyShowBirthYear={onlyShowBirthYear} setOnlyShowBirthYear={setOnlyShowBirthYear}
        onlyShowBirthMonth={onlyShowBirthMonth} setOnlyShowBirthMonth={setOnlyShowBirthMonth}
        dispositionTags={dispositionTags} setDispositionTags={setDispositionTags} dispositionOptions={dispositionOptions}
        excludeDispositions={excludeDispositions} setExcludeDispositions={setExcludeDispositions}
        search={search} setSearch={setSearch}
        statuses={statuses} setStatuses={setStatuses}
        savedSnapshot={savedSnapshot} onApplySaved={handleApplySaved}
        setPage={setPage}
        onManageDispositions={() => setShowCustomDispoModal(true)}
        onResetFilters={handleResetFilters}
      />

      {/* Bulk action bar */}
      <LeadsBulkActionBar
        selected={selected}
        allMatching={allMatching}
        clearSelection={clearSelection}
        orderedSelectedIds={orderedSelectedIds}
        leads={leads}
        onOpenAiDial={() => setAiDialOpen(true)}
        bulkMenuOpen={bulkMenuOpen}
        setBulkMenuOpen={setBulkMenuOpen}
        bulkAction={bulkAction}
        bulkAssignTextBot={bulkAssignTextBot}
        setDispoToBotOpen={setDispoToBotOpen}
        setAddToPipelineOpen={setAddToPipelineOpen}
        setTransferToLeadAssistOpen={setTransferToLeadAssistOpen}
        aiBots={aiBots}
        setBulkSmsOpen={setBulkSmsOpen}
        setBulkEmailOpen={setBulkEmailOpen}
        openCsvWizard={openCsvWizard}
        agents={agents}
        dispositions={dispositions}
        vendors={vendorsData?.vendors || []}
        drips={drips}
        campaigns={campaigns}
        pipelines={pipelines}
      />

      {/* Lead list */}
      <LeadsTable
        leads={leads}
        total={total}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        isLoading={isLoading}
        selected={selected}
        allMatching={allMatching}
        selectingAll={selectingAll}
        toggleSelectAll={toggleSelectAll}
        toggleSelect={toggleSelect}
        clearSelection={clearSelection}
        selectAllMatching={selectAllMatching}
        setPage={setPage}
        dispositions={dispositions}
        drips={drips}
        onOpenLead={(id) => navigate(`/leads/${id}`)}
        onDispositionApplied={() => qc.invalidateQueries({ queryKey: ['leads'] })}
      />

      {/* Modals */}
      {showCreate && (
        <CreateLeadModal
          vendors={vendorsData?.vendors || []}
          onClose={() => setShowCreate(false)}
          onCreated={() => qc.invalidateQueries({ queryKey: ['leads'] })}
        />
      )}

      {showCustomDispoModal && (
        <ManageDispositionsModal
          dispositions={dispositions}
          onClose={() => setShowCustomDispoModal(false)}
          onChange={() => refetchDispos()}
        />
      )}

      <AIDialSelectedModal
        open={aiDialOpen}
        onClose={() => setAiDialOpen(false)}
        leadIds={orderedSelectedIds}
        contextLabel="Quick Dial"
        onLaunched={clearSelection}
      />

      {dispoToBotOpen && (
        <DispositionToBotModal
          count={selected.size}
          leadIds={orderedSelectedIds}
          onClose={() => setDispoToBotOpen(false)}
          onDone={() => {
            setDispoToBotOpen(false);
            clearSelection();
            qc.invalidateQueries({ queryKey: ['leads'] });
          }}
        />
      )}

      {addToPipelineOpen && (
        <AddToPipelineModal
          count={selected.size}
          leadIds={orderedSelectedIds}
          pipelines={pipelines}
          onClose={() => setAddToPipelineOpen(false)}
          onDone={() => {
            setAddToPipelineOpen(false);
            clearSelection();
            qc.invalidateQueries({ queryKey: ['leads'] });
          }}
        />
      )}

      {transferToLeadAssistOpen && (
        <TransferToLeadAssistModal
          count={selected.size}
          leadIds={orderedSelectedIds}
          onClose={() => setTransferToLeadAssistOpen(false)}
          onDone={() => {
            setTransferToLeadAssistOpen(false);
            clearSelection();
            qc.invalidateQueries({ queryKey: ['leads'] });
          }}
        />
      )}

      {bulkSmsOpen && (
        <BulkSmsModal
          count={selected.size}
          onClose={() => setBulkSmsOpen(false)}
          onSend={async (body) => {
            try {
              const res = await apiPost('/leads/bulk/action', {
                ids: orderedSelectedIds,
                action: 'bulkSms',
                payload: { body },
              });
              toast.success(`Sent to ${res.modified} of ${res.matched}` + (res.failed ? ` — ${res.failed} failed` : ''));
              setBulkSmsOpen(false);
              clearSelection();
            } catch (e) {
              toast.error(e.response?.data?.error || 'Bulk SMS failed');
            }
          }}
        />
      )}

      {bulkEmailOpen && (
        <BulkEmailModal
          count={selected.size}
          onClose={() => setBulkEmailOpen(false)}
          onSend={async (subject, body) => {
            try {
              const res = await apiPost('/leads/bulk/action', {
                ids: orderedSelectedIds,
                action: 'bulkEmail',
                payload: { subject, body },
              });
              toast.success(res.note || `Queued for ${res.queued || res.matched} lead(s)`);
              setBulkEmailOpen(false);
              clearSelection();
            } catch (e) {
              toast.error(e.response?.data?.error || 'Bulk email failed');
            }
          }}
        />
      )}

      {csvWizardOpen && (
        <CsvExportWizard
          ids={csvWizardIds}
          totalFiltered={total}
          baseParams={exportParamsFromFilters()}
          onClose={() => { setCsvWizardOpen(false); setCsvWizardIds(null); }}
        />
      )}
    </div>
  );
}
